import type { SituationSource } from "@peer-start/platform-contracts";
import { Button } from "@peer-start/ui";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@peer-start/ui/reducate";
import { Check, Plus, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSituationSources } from "../api/situation-card-queries.js";
import { SituationSourceSummary } from "./SituationSourceSummary.js";

type SearchType = "" | "cursus" | "url" | "pdf";

export function SituationSourceLibrary({
  attachedIds,
  pending,
  onSave,
  onClose,
  onDragging,
}: {
  attachedIds: string[];
  pending: boolean;
  onSave: (sourceIds: string[]) => void;
  onClose: () => void;
  onDragging: (dragging: boolean) => void;
}) {
  const { t } = useTranslation();
  const searchInput = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState<SearchType>("");
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState<string[]>([]);
  const [dragging, setDragging] = useState(false);
  const [seed] = useState(() => Math.floor(Math.random() * 1_000_000_000) + 1);
  const searching = q.trim().length > 0;
  const results = useSituationSources({ page, q, type, seed: searching ? undefined : seed }, true);
  const pages = Math.max(1, Math.ceil((results.data?.total ?? 0) / 10));
  const items: SituationSource[] = results.data?.items ?? [];
  const loaded = results.isSuccess;

  useEffect(() => {
    const overlay = document.querySelector<HTMLElement>("[data-slot='dialog-overlay']");
    if (!overlay) return;
    overlay.style.pointerEvents = dragging ? "none" : "";
  }, [dragging]);

  function setDrag(next: boolean) {
    setDragging(next);
    onDragging(next);
  }

  return (
    <Dialog
      modal={false}
      onOpenChange={(open) => {
        if (!open && !dragging) onClose();
      }}
      open
    >
      <DialogContent
        className="flex max-h-[80vh] flex-col sm:max-w-4xl"
        closeLabel={t("common.close")}
        onInteractOutside={(event) => {
          if (dragging) event.preventDefault();
        }}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          searchInput.current?.focus();
        }}
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle>{t("administration.situationCard.addSources")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative min-w-0 flex-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              aria-label={t("administration.situationCard.sourceSearch")}
              className="pl-8"
              data-control-id="situation.sources.search"
              onChange={(event) => {
                setQ(event.target.value);
                setPage(1);
              }}
              placeholder={t("administration.situationCard.sourceSearch")}
              ref={searchInput}
              type="search"
              value={q}
            />
          </div>
          <Select
            onValueChange={(next) => {
              setType(next === "all" ? "" : (next as SearchType));
              setPage(1);
            }}
            value={type || "all"}
          >
            <SelectTrigger
              aria-label={t("administration.situationCard.sourceType")}
              className="w-full sm:w-auto"
              data-control-id="situation.sources.type"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="popper">
              <SelectItem value="all">{t("administration.situationCard.allTypes")}</SelectItem>
              <SelectItem value="cursus">{t("administration.situationCard.sourceKind.cursus")}</SelectItem>
              <SelectItem value="url">{t("administration.situationCard.sourceKind.url")}</SelectItem>
              <SelectItem value="pdf">{t("administration.situationCard.sourceKind.pdf")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {searching ? null : (
          <h3 className="mt-5 text-sm font-medium text-muted-foreground">
            {t("administration.situationCard.allSources")}
          </h3>
        )}
        <ul className={`${searching ? "mt-4" : "mt-2"} min-h-0 flex-1 divide-y divide-border overflow-y-auto`}>
          {items.map((source) => {
            const attached = attachedIds.includes(source.id) || picked.includes(source.id);
            return (
              <li
                className="flex items-center gap-3 py-2.5"
                draggable={!attached}
                key={source.id}
                onDragEnd={() => setDrag(false)}
                onDragStart={(event) => {
                  event.dataTransfer.setData("text/plain", source.id);
                  event.dataTransfer.effectAllowed = "copy";
                  setDrag(true);
                }}
              >
                <SituationSourceSummary href={source.href} tag={source.tag} title={source.title} />
                {attached ? (
                  <span
                    aria-label={t("administration.situationCard.attached")}
                    className="grid size-6 shrink-0 place-items-center rounded-md border border-success-foreground/20 bg-success text-success-foreground"
                    role="img"
                    title={t("administration.situationCard.attached")}
                  >
                    <Check aria-hidden="true" className="size-3.5" />
                  </span>
                ) : (
                  <button
                    aria-label={t("administration.situationCard.addSourceNamed", { title: source.title })}
                    className="grid size-6 shrink-0 place-items-center rounded-md border border-input bg-secondary text-secondary-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30 disabled:opacity-50"
                    data-control-id={`situation.sources.add.${source.id}`}
                    disabled={pending}
                    onClick={() => setPicked((current) => (current.includes(source.id) ? current : [...current, source.id]))}
                    type="button"
                  >
                    <Plus aria-hidden="true" className="size-3.5" />
                  </button>
                )}
              </li>
            );
          })}
          {loaded && items.length === 0 ? (
            <li className="py-6 text-sm text-muted-foreground">{t("administration.situationCard.noSources")}</li>
          ) : null}
        </ul>
        {loaded ? (
          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              controlId="situation.sources.previous"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
              type="button"
              variant="secondary"
            >
              {t("administration.situationCard.previous")}
            </Button>
            <span className="text-xs text-muted-foreground">{t("administration.situationCard.page", { page, pages })}</span>
            <Button
              controlId="situation.sources.next"
              disabled={page >= pages}
              onClick={() => setPage((current) => current + 1)}
              type="button"
              variant="secondary"
            >
              {t("administration.situationCard.next")}
            </Button>
          </div>
        ) : null}
        <DialogFooter>
          <Button controlId="situation.sources.cancel" onClick={onClose} type="button" variant="secondary">
            {t("administration.situationCard.reject")}
          </Button>
          <Button
            controlId="situation.sources.save"
            disabled={pending || picked.length === 0}
            onClick={() => onSave([...picked].reverse().concat(attachedIds))}
            type="button"
          >
            {t("administration.situationCard.publish")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
